/**
 * useEncounterAuditsList Hook
 *
 * Fetches and manages encounter audits list with pagination
 * Supports both provider and admin endpoints via organizationId parameter
 */

import { useMemo } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';

import {
  useGetAdminEncountersQuery,
  useGetDashboardEncountersQuery,
} from '../store/api';
import type { EncounterAuditSummary } from '../types';

interface UseEncounterAuditsListOptions {
  /** Organization ID for admin mode (if provided, uses admin endpoint) */
  organizationId?: string;
  /** Comma-separated user IDs to filter by */
  userIds?: string;
  /** Filter by encounters with/without audits */
  hasAudit?: boolean;
  /** Current page number (1-indexed) */
  page?: number;
  /** Items per page */
  perPage?: number;
  /** Period in days */
  days?: number;
  /** Explicit start date (ISO 8601 format) */
  dateFrom?: string;
  /** Explicit end date (ISO 8601 format) */
  dateTo?: string;
}

interface UseEncounterAuditsListResult {
  /** List of encounter audits */
  encounters: EncounterAuditSummary[];
  /** Total number of encounters */
  total: number;
  /** Current page */
  page: number;
  /** Items per page */
  perPage: number;
  /** Whether there are more items */
  hasMore: boolean;
  /** Whether data is loading */
  isLoading: boolean;
  /** Whether data is being fetched (including refetches) */
  isFetching: boolean;
  /** Error message if any */
  error: string | null;
  /** Refetch function */
  refetch: () => void;
  /** Whether list is empty */
  isEmpty: boolean;
}

export const useEncounterAuditsList = (
  options: UseEncounterAuditsListOptions = {},
): UseEncounterAuditsListResult => {
  // Build query params from options using generated types
  const queryParams: {
    hasAudit?: boolean | null;
    page?: number;
    perPage?: number;
    days?: number | null;
    dateFrom?: string | null;
    dateTo?: string | null;
    userIds?: string | null;
  } = {};

  if (options.hasAudit !== undefined) {
    queryParams.hasAudit = options.hasAudit;
  }
  if (options.page !== undefined) {
    queryParams.page = options.page;
  }
  if (options.perPage !== undefined) {
    queryParams.perPage = options.perPage;
  }
  if (options.days !== undefined) {
    queryParams.days = options.days;
  }
  if (options.dateFrom) {
    queryParams.dateFrom = options.dateFrom;
  }
  if (options.dateTo) {
    queryParams.dateTo = options.dateTo;
  }
  if (options.userIds) {
    queryParams.userIds = options.userIds;
  }

  // Use admin endpoint if organizationId provided, otherwise use provider endpoint
  const adminQuery = useGetAdminEncountersQuery(
    options.organizationId
      ? {
          ...queryParams,
          organizationId: options.organizationId,
        }
      : skipToken,
  );

  const providerQuery = useGetDashboardEncountersQuery(
    !options.organizationId
      ? (Object.keys(queryParams).length > 0 ? queryParams : ({} as any))
      : skipToken,
  );

  // Use whichever query is active
  const { data, isLoading, isFetching, error, refetch } = options.organizationId
    ? adminQuery
    : providerQuery;

  const encounters = useMemo(() => data?.encounters || [], [data]);
  const total = data?.total || 0;
  const page = data?.page || 1;
  const perPage = data?.per_page || 20;
  const hasMore = data?.has_more || false;
  const isEmpty = !isLoading && encounters.length === 0;

  return {
    encounters,
    total,
    page,
    perPage,
    hasMore,
    isLoading,
    isFetching,
    error: error ? 'Failed to load encounter audits' : null,
    refetch,
    isEmpty,
  };
};

export default useEncounterAuditsList;
